import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { GOOGLE_REVIEWS } from "@/lib/google-reviews";
import {
  getReviewStats,
  listPublishedReviews,
  submitReview,
  type PublishedReview,
  type ReviewStats,
} from "@/lib/reviews.functions";


const field =
  "w-full rounded-sm border border-input bg-card px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/20";

function Stars({ rating }: { rating: number }) {
  const filled = Math.round(rating);
  return (
    <span aria-label={`${rating} out of 5`} className="text-primary">
      {"★".repeat(filled)}
      <span className="text-muted-foreground/40">{"★".repeat(Math.max(0, 5 - filled))}</span>
    </span>
  );
}

/**
 * Compact review block for service pages. Shows the published rating and the
 * two most recent approved reviews, and lets a client leave one — approved
 * reviews feed the aggregate rating shown in the homepage schema.
 */
export function ReviewStrip({
  heading = "Reviews from clients",
  suburb,
  matterType = "Conveyancing",
}: {
  heading?: string;
  suburb?: string;
  matterType?: string;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [reviews, setReviews] = useState<PublishedReview[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const [list, s] = await Promise.all([listPublishedReviews(), getReviewStats()]);
        if (!live) return;
        setReviews(list.slice(0, 2));
        setStats(s);
      } catch {
        /* the section simply stays in its invitation state */
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);
    setSending(true);
    try {
      await submitReview({
        data: {
          name: String(fd.get("name") ?? ""),
          email: String(fd.get("email") ?? ""),
          suburb: String(fd.get("suburb") ?? ""),
          matterType: matterType,
          rating,
          body: String(fd.get("body") ?? ""),
          consent: fd.get("consent") === "on",
          sourcePage: pathname,
        },
      });
      setSent(true);
      form.reset();
      toast.success("Thank you — your review has come through to us.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send your review.");
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="mt-14 rounded-sm border border-border bg-secondary/40 p-6 sm:p-7">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-serif text-2xl text-foreground">{heading}</h2>
        {stats && stats.count > 0 ? (
          <p className="text-sm text-muted-foreground">
            <Stars rating={stats.average} />{" "}
            <span className="font-medium text-foreground">{stats.average.toFixed(1)}</span> from{" "}
            {stats.count} client {stats.count === 1 ? "review" : "reviews"}
          </p>
        ) : null}
      </div>

      <p className="mt-2 text-sm text-muted-foreground">
        <Stars rating={5} /> <span className="font-medium text-foreground">5.0</span> from{" "}
        {GOOGLE_REVIEWS.length} Google reviews
      </p>
      <div className="review-marquee relative -mx-6 mt-5 overflow-hidden sm:-mx-7">
        <ul className="review-marquee-track flex w-max gap-4 px-6 sm:px-7">
          {[...GOOGLE_REVIEWS, ...GOOGLE_REVIEWS].map((review, i) => (
            <li
              key={`${review.id}-${i}`}
              aria-hidden={i >= GOOGLE_REVIEWS.length}
              className="w-[300px] shrink-0 rounded-sm border border-border bg-card p-5 shadow-sm sm:w-[340px]"
            >
              <Stars rating={review.rating} />
              <p className="mt-3 line-clamp-6 text-base leading-relaxed text-muted-foreground">
                “{review.body}”
              </p>
              <p className="mt-4 text-sm font-semibold text-foreground">
                {review.name}
                <span className="font-normal text-muted-foreground"> · Google review</span>
              </p>
            </li>
          ))}
        </ul>
      </div>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">
        {reviews.slice(0, 2).map((review) => (
          <li key={review.id} className="rounded-sm border border-border bg-card p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-foreground">
                {review.name}
                {review.suburb ? (
                  <span className="font-normal text-muted-foreground"> · {review.suburb}</span>
                ) : null}
              </p>
              <Stars rating={review.rating} />
            </div>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">{review.body}</p>
          </li>
        ))}
      </ul>
      {reviews.length === 0 ? (
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          If we have acted for you{suburb ? ` in ${suburb}` : ""}, a short review helps other local
          buyers and sellers work out who to trust. Reviews appear on the site once we have checked
          them.
        </p>
      ) : null}


      <div className="mt-5 flex flex-wrap gap-3">
        {!open && !sent ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex h-[42px] items-center justify-center rounded-sm bg-primary px-5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            Leave a review
          </button>
        ) : null}
        <Link
          to="/reviews"
          className="inline-flex h-[42px] items-center justify-center rounded-sm border border-border px-5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          Read all reviews
        </Link>
      </div>

      {sent ? (
        <p className="mt-5 text-sm leading-relaxed text-foreground">
          Thank you. We read every review and publish it here once we have checked it.
        </p>
      ) : null}

      {open && !sent ? (
        <form onSubmit={onSubmit} className="mt-6 grid gap-4 sm:grid-cols-2">
          <label className="text-sm">
            <span className="block font-medium text-foreground">Your name</span>
            <input name="name" required minLength={2} className={`mt-1.5 ${field}`} />
          </label>
          <label className="text-sm">
            <span className="block font-medium text-foreground">Email</span>
            <input name="email" type="email" required className={`mt-1.5 ${field}`} />
          </label>
          <label className="text-sm">
            <span className="block font-medium text-foreground">Suburb</span>
            <input
              name="suburb"
              defaultValue={suburb ?? ""}
              className={`mt-1.5 ${field}`}
              placeholder="Golden Beach"
            />
          </label>
          <label className="text-sm">
            <span className="block font-medium text-foreground">Rating</span>
            <select
              value={rating}
              onChange={(e) => setRating(Number(e.target.value))}
              className={`mt-1.5 ${field}`}
            >
              {[5, 4, 3, 2, 1].map((r) => (
                <option key={r} value={r}>
                  {r} star{r === 1 ? "" : "s"}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="block font-medium text-foreground">Your review</span>
            <textarea
              name="body"
              required
              minLength={20}
              rows={4}
              className={`mt-1.5 ${field}`}
              placeholder="How did we go with your matter?"
            />
          </label>
          <label className="flex items-start gap-2 text-sm sm:col-span-2">
            <input name="consent" type="checkbox" defaultChecked className="mt-1" />
            <span className="text-muted-foreground">
              I am happy for Coastside Law to publish this review, with my first name and suburb, on
              its website.
            </span>
          </label>
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={sending}
              className="inline-flex h-[42px] items-center justify-center rounded-sm bg-primary px-6 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send review"}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
